import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {Router, RouterModule, NavigationEnd, ActivatedRoute} from '@angular/router';
import {TemplateService} from './template.service';
import {CompanyName} from '../models/CompanyName';
import {lastValueFrom} from "rxjs";
import {MatMenuModule} from "@angular/material/menu";
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule, MatDrawer } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxSpinnerModule } from 'ngx-spinner';
import { filter } from 'rxjs/operators';
import {DashboardService} from "../dashboard/dashboard.service";
import {GatewayStatus} from "../models/Dashboard";
import {getBaseUrl, getDatabase} from '../../environments/environment-functions';
import { MyProfileService } from '../my-profile/my-profile.service';
import {AutenticacaoExpressaService} from '../autenticacao-expressa/autenticacao-expressa.service';

@Component({
  selector: 'app-template',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatSidenavModule,
    MatListModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    NgxSpinnerModule
  ],
  templateUrl: './template.component.html',
  styleUrls: ['./template.component.css', '../backgrounds.css']
})
export class TemplateComponent implements OnInit, OnDestroy {
  private readonly themeStorageKey = 'client-area-theme';
  private mediaQueryList?: MediaQueryList;
  private mediaQueryListener?: (event: MediaQueryListEvent) => void;

  @ViewChild('drawer') drawer!: MatDrawer;

  constructor(
    private toaster: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private service: TemplateService,
    private dashboardService: DashboardService,
    private myProfileService: MyProfileService,
    private autenticacaoExpressaService: AutenticacaoExpressaService,
  ) {
    // Listen to route changes to update active state
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateActiveRoute();
    });
  }

  shouldRun: boolean = true;
  companyName: string = '';
  clientName: string = '';
  clientEmail: string = '';
  isMobile: boolean = false;
  currentRoute: string = '';
  themeLabel: string = 'Tema claro';
  currentTheme: 'light' | 'dark' = 'light';
  gatewayStatus: GatewayStatus = {
    gatewayActive: false,
  };

  async ngOnInit(): Promise<void> {
    try {
      this.checkScreenSize();
      this.updateActiveRoute();
      this.bindThemeLabel();
      await this.authenticateTokenFromUrl();

      if (!this.checkIsLogged()) {
        return;
      }

      const [gatewayStatus, company, dashboard, profile] = await Promise.all([
        lastValueFrom(this.dashboardService.getGatewayActive()),
        this.getCompanyName(),
        lastValueFrom(this.dashboardService.getStatistics()),
        lastValueFrom(this.myProfileService.getMyProfile()),
      ]);

      this.gatewayStatus = gatewayStatus;
      this.companyName = company?.name || '';
      this.clientName = dashboard?.NOME || '';
      this.clientEmail = profile?.EMAIL || '';
    } catch (err) {
        console.error(err);
        this.toaster.error('Erro ao carregar cabeçalho da área do cliente');
        await this.router.navigate(['/login']);
    }
  }

  ngOnDestroy(): void {
    if (this.mediaQueryList && this.mediaQueryListener) {
      this.mediaQueryList.removeEventListener('change', this.mediaQueryListener);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  private bindThemeLabel(): void {
    this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
    const savedTheme = localStorage.getItem(this.themeStorageKey) as 'light' | 'dark' | null;

    this.applyTheme(savedTheme ?? (this.mediaQueryList.matches ? 'dark' : 'light'));

    this.mediaQueryListener = (event: MediaQueryListEvent) => {
      if (localStorage.getItem(this.themeStorageKey)) {
        return;
      }

      this.applyTheme(event.matches ? 'dark' : 'light');
    };

    this.mediaQueryList.addEventListener('change', this.mediaQueryListener);
  }

  private updateActiveRoute(): void {
    this.currentRoute = this.router.url;
  }

  private async authenticateTokenFromUrl(): Promise<void> {
    const token = this.route.snapshot.queryParamMap.get('token');

    if (!token) {
      return;
    }

    await this.autenticacaoExpressaService.autenticarToken(token);
  }

  public isActiveRoute(route: string): boolean {
    return this.currentRoute.includes(route);
  }

  public async redirect(uri: string): Promise<any> {
    await this.router.navigate([uri]);
    if (this.drawer && this.isMobile) {
      this.drawer.close();
    }
  }

  public logout(): void {
    localStorage.clear();
    this.toaster.success('Logout realizado com sucesso', 'Até logo!');
    this.router.navigate(['/login']);
  }

  private checkIsLogged(): boolean{
    if (!localStorage.getItem('x-session-token-ac')){
      this.toaster.warning('Faça login novamente', 'Desconectado');
      this.router.navigate(['/login']);
      return false;
    }

    return true;
  }

  public async getCompanyName(): Promise<CompanyName> {
    return await lastValueFrom(this.service.getCompanyName());
  }

  public logoUrl(): string {
    return getBaseUrl() + 'client_area/logo?database=' + getDatabase();
  }

  public get userInitials(): string {
    const name = this.clientName?.trim();
    if (!name) {
      return 'JS';
    }

    return name
      .split(/\s+/)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('');
  }

  public openSupport(): void {
    this.toaster.info('Entre em contato com a sua unidade para obter suporte.');
  }

  public toggleTheme(): void {
    const nextTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
    localStorage.setItem(this.themeStorageKey, nextTheme);
    this.applyTheme(nextTheme);
  }

  public get themeIcon(): string {
    return this.currentTheme === 'dark' ? 'dark_mode' : 'light_mode';
  }

  private applyTheme(theme: 'light' | 'dark'): void {
    this.currentTheme = theme;
    this.themeLabel = theme === 'dark' ? 'Tema escuro' : 'Tema claro';

    document.body.classList.toggle('theme-dark', theme === 'dark');
    document.body.classList.toggle('theme-light', theme === 'light');
  }
}
